import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getRestaurants, createRestaurant, getUsers, getOrders, getTemplates, deleteRestaurant, updateRestaurant } from '../services/api';
import AppShell from '../components/layout/AppShell';
import PageHeader from '../components/layout/PageHeader';
import KpiCard from '../components/ui/KpiCard';
import StatusPill from '../components/ui/StatusPill';
import { Storefront, PeopleAlt, ReceiptLong } from '../components/ui/Icons';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user, restaurants, setRestaurants, isAdmin, isManager } = useAuth();
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState({ restaurants: 0, users: 0, orders: 0 });
  const [loading, setLoading] = useState(true);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showNewRestaurant, setShowNewRestaurant] = useState(false);
  const [restaurantFilter, setRestaurantFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultDialogMessage, setResultDialogMessage] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmRestaurant, setDeleteConfirmRestaurant] = useState(null);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [toggleConfirmRestaurant, setToggleConfirmRestaurant] = useState(null);
  const [toggleConfirmAction, setToggleConfirmAction] = useState('');

  useEffect(() => {
    if (isAdmin() || isManager()) {
      fetchDashboardData();
      fetchTemplates();
    }
  }, [isAdmin, isManager]);

  const fetchTemplates = async () => {
    try {
      const response = await getTemplates();
      console.log('Templates API response:', response);
      // Axios wraps the response in a data property
      if (response && response.data) {
        console.log('Setting templates:', response.data);
        setTemplates(Array.isArray(response.data) ? response.data : []);
      } else {
        console.warn('Templates response format unexpected:', response);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      console.error('Error details:', error.response?.data || error.message);
      setTemplates([]);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const promises = [getRestaurants()];
      if (isAdmin()) {
        promises.push(getUsers());
      }
      const results = await Promise.all(promises);
      const restaurantsRes = results[0];
      const usersRes = isAdmin() ? results[1] : { data: [] };
      
      // Ensure status is set for all restaurants (default to 'active' if null/undefined)
      const restaurants = restaurantsRes.data.map(r => {
        const status = (r.status || 'active').toLowerCase(); // Normalize to lowercase
        console.log(`Restaurant ${r.id} (${r.name}): status = "${r.status}" -> normalized: "${status}"`);
        return {
          ...r,
          status: status
        };
      });
      
      console.log('Fetched restaurants:', restaurants);
      const inactive = restaurants.filter(r => r.status === 'inactive');
      console.log('Inactive restaurants:', inactive);
      console.log('Inactive count:', inactive.length);
      
      setAllRestaurants(restaurants);
      if (isAdmin()) {
        setAllUsers(usersRes.data);
      }
      
      // Calculate stats
      let totalOrders = 0;
      for (const restaurant of restaurants) {
        try {
          const ordersRes = await getOrders(restaurant.id);
          totalOrders += (ordersRes.data.total ?? ordersRes.data.orders?.length ?? ordersRes.data.length ?? 0);
        } catch (e) {
          // Ignore errors for individual restaurant orders
        }
      }
      
      setStats({
        restaurants: restaurantsRes.data.length,
        users: isAdmin() ? usersRes.data.length : 0,
        orders: totalOrders
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRestaurant = async (e) => {
    e.preventDefault();
    if (!newRestaurantName.trim()) return;

    try {
      const restaurantData = {
        name: newRestaurantName.trim(),
        templateId: selectedTemplate || null
      };
      
      const createRes = await createRestaurant(restaurantData);
      setNewRestaurantName('');
      setSelectedTemplate('');
      setShowNewRestaurant(false);

      // Refresh AuthContext.restaurants so the Admin page dropdown
      // (and any other consumer of useAuth().restaurants) sees the new entry.
      const restaurantsRes = await getRestaurants();
      setRestaurants(restaurantsRes.data);

      await fetchDashboardData();

      const baseMsg = selectedTemplate
        ? t('dashboard.admin.newRestaurant.successWithTemplate')
        : t('dashboard.admin.newRestaurant.successMessage');
      const slug = createRes?.data?.slug;
      const slugMsg = slug
        ? `\n${t('dashboard.admin.newRestaurant.slugLink', { url: `${window.location.origin}/menu/${slug}` })}`
        : '';
      setResultDialogMessage(baseMsg + slugMsg);
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.createRestaurant'));
      setResultDialogOpen(true);
    }
  };

  const handleDeleteRestaurantClick = (restaurantId, restaurantName) => {
    setDeleteConfirmRestaurant({ id: restaurantId, name: restaurantName });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteRestaurantConfirm = async () => {
    if (!deleteConfirmRestaurant) return;
    const { id, name } = deleteConfirmRestaurant;
    setDeleteConfirmOpen(false);
    setDeleteConfirmRestaurant(null);
    try {
      await deleteRestaurant(id);
      const restaurantsRes = await getRestaurants();
      setRestaurants(restaurantsRes.data);
      await fetchDashboardData();
      setResultDialogMessage(t('api.success.restaurantDeleted'));
      setResultDialogOpen(true);
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.deleteRestaurant'));
      setResultDialogOpen(true);
    }
  };

  const handleToggleRestaurantClick = (restaurantId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'enable' : 'disable';
    setToggleConfirmRestaurant({ id: restaurantId, currentStatus, newStatus });
    setToggleConfirmAction(action);
    setToggleConfirmOpen(true);
  };

  const handleToggleRestaurantConfirm = async () => {
    if (!toggleConfirmRestaurant) return;
    const { id, newStatus } = toggleConfirmRestaurant;
    const action = newStatus === 'active' ? 'enable' : 'disable';
    setToggleConfirmOpen(false);
    setToggleConfirmRestaurant(null);
    try {
      await updateRestaurant(id, { status: newStatus });
      const restaurantsRes = await getRestaurants();
      setRestaurants(restaurantsRes.data);
      await fetchDashboardData();
      setResultDialogMessage(t('api.success.restaurantUpdated'));
      setResultDialogOpen(true);
    } catch (error) {
      console.error('Error updating restaurant status:', error);
      setResultDialogMessage(error.response?.data?.error || t('api.errors.updateRestaurant'));
      setResultDialogOpen(true);
    }
  };

  if (!isAdmin() && !isManager()) {
    return (
      <AppShell>
        <div className="error-container">
          <h2>{t('dashboard.admin.accessDenied.title')}</h2>
          <p>{t('dashboard.admin.accessDenied.message')}</p>
        </div>
      </AppShell>
    );
  }

  // For managers, check if they can create a restaurant
  const canCreateRestaurant = isAdmin() || (isManager() && restaurants.length === 0);

  return (
    <AppShell>
      <PageHeader
        title={t('dashboard.admin.title')}
        subtitle={t('dashboard.admin.welcome', { name: user?.name })}
      />

      {/* KPI row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2.5,
          mb: 4,
        }}
      >
        <KpiCard
          icon={<Storefront size={22} />}
          iconTone="primary"
          label={t('dashboard.admin.stats.restaurants')}
          value={stats.restaurants}
        />
        <KpiCard
          icon={<PeopleAlt size={22} />}
          iconTone="info"
          label={t('dashboard.admin.stats.users')}
          value={stats.users}
        />
        <KpiCard
          icon={<ReceiptLong size={22} />}
          iconTone="success"
          label={t('dashboard.admin.stats.totalOrders')}
          value={stats.orders}
        />
      </Box>

      <div className="dashboard-container">


        {/* Quick Actions */}
        <div className="quick-actions">
          {canCreateRestaurant && (
            <button 
              className="btn btn-primary"
              onClick={() => {
                setShowNewRestaurant(!showNewRestaurant);
                if (!showNewRestaurant && templates.length === 0) {
                  fetchTemplates();
                }
              }}
            >
              {t('dashboard.admin.createRestaurant')}
            </button>
          )}
          {isAdmin() && (
            <>
              <Link to="/admin/users" className="btn btn-secondary">
                {t('dashboard.admin.manageUsers')}
              </Link>
              <Link to="/admin/subscriptions" className="btn btn-secondary">
                {t('admin.subscriptions.title')}
              </Link>
            </>
          )}
          <Link to="/admin/analytics" className="btn btn-secondary">
            {t('analytics.admin.platformTitle')}
          </Link>
          <Link to="/admin" className="btn btn-secondary">
            {t('dashboard.admin.menuManagement')}
          </Link>
        </div>

        {showNewRestaurant && canCreateRestaurant && (
          <div className="new-restaurant-form">
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>{t('dashboard.admin.newRestaurant.title')}</h3>
            {isManager() && restaurants.length > 0 && (
              <div style={{ 
                padding: '0.75rem', 
                marginBottom: '1rem', 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '4px',
                color: '#856404'
              }}>
                {t('dashboard.admin.newRestaurant.managerWarning')}
              </div>
            )}
            <form onSubmit={handleCreateRestaurant}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>{t('dashboard.admin.newRestaurant.nameLabel')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={newRestaurantName}
                  onChange={(e) => setNewRestaurantName(e.target.value)}
                  placeholder={t('dashboard.admin.newRestaurant.namePlaceholder')}
                  autoFocus
                  required
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>{t('dashboard.admin.newRestaurant.templateLabel')}</label>
                  <select
                    className="form-input"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    <option value="">{t('dashboard.admin.newRestaurant.noTemplate')}</option>
                    {templates && templates.length > 0 ? (
                      templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} ({template.itemCount || template.items?.length || 0} {t('common.items')})
                        </option>
                      ))
                    ) : (
                      <option disabled>{t('dashboard.admin.newRestaurant.loadingTemplates')}</option>
                    )}
                  </select>
                  <small style={{ color: 'var(--text-muted)', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                    {t('dashboard.admin.newRestaurant.templateHint')}
                  </small>
                </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" className="btn btn-primary">{t('dashboard.admin.newRestaurant.createButton')}</button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowNewRestaurant(false);
                    setNewRestaurantName('');
                    setSelectedTemplate('');
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Restaurants List */}
        <div className="dashboard-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0 }}>{t('dashboard.admin.restaurants.title')}</h2>
            {isAdmin() && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className={`btn ${restaurantFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setRestaurantFilter('all')}
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  {t('dashboard.admin.restaurants.filterAll')} ({allRestaurants.length})
                </button>
                <button
                  className={`btn ${restaurantFilter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setRestaurantFilter('active')}
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  {t('dashboard.admin.restaurants.filterActive')} ({allRestaurants.filter(r => (r.status || 'active') === 'active').length})
                </button>
                <button
                  className={`btn ${restaurantFilter === 'inactive' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setRestaurantFilter('inactive')}
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  {t('dashboard.admin.restaurants.filterDisabled')} ({allRestaurants.filter(r => (r.status || 'active') === 'inactive').length})
                </button>
              </div>
            )}
          </div>
          {loading ? (
            <div className="loading-container">
              <div className="loading"></div>
            </div>
          ) : (() => {
            const filteredRestaurants = allRestaurants.filter(restaurant => {
              const status = restaurant.status || 'active';
              if (restaurantFilter === 'all') return true;
              if (restaurantFilter === 'active') return status === 'active';
              if (restaurantFilter === 'inactive') return status === 'inactive';
              return true;
            });

            if (filteredRestaurants.length === 0) {
              return (
                <div className="empty-state">
                  <div className="empty-state-icon">🍽️</div>
                  <h3>{t('dashboard.admin.restaurants.emptyState')}</h3>
                  <p>
                    {restaurantFilter === 'inactive' 
                      ? t('dashboard.admin.restaurants.noDisabled')
                      : restaurantFilter === 'active'
                      ? t('dashboard.admin.restaurants.noActive')
                      : t('dashboard.admin.restaurants.noRestaurants')}
                  </p>
                </div>
              );
            }

            return (
              <div className="restaurants-grid">
                {filteredRestaurants.map(restaurant => (
                <div key={restaurant.id} className="restaurant-card">
                  <div className="restaurant-card-header">
                    <h3>{restaurant.name}</h3>
                    <StatusPill
                      status={restaurant.status || 'active'}
                      label={t(`common.${restaurant.status || 'active'}`)}
                      dot
                    />
                  </div>
                  <p>{t('dashboard.admin.restaurants.id')}: {restaurant.id}</p>
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    marginTop: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <Link 
                      to={`/admin?restaurant=${restaurant.id}`}
                      className="btn btn-primary btn-small"
                      style={{ textDecoration: 'none', flex: 1 }}
                    >
                      {t('dashboard.admin.restaurants.manage')}
                    </Link>
                    {isAdmin() && (
                      <>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleToggleRestaurantClick(restaurant.id, restaurant.status || 'active')}
                          title={restaurant.status === 'inactive' ? t('dashboard.admin.restaurants.enable') : t('dashboard.admin.restaurants.disable')}
                        >
                          {restaurant.status === 'inactive' ? t('dashboard.admin.restaurants.enable') : t('dashboard.admin.restaurants.disable')}
                        </button>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleDeleteRestaurantClick(restaurant.id, restaurant.name)}
                          style={{ background: '#dc3545', color: 'white', borderColor: '#dc3545' }}
                          title={t('common.delete')}
                        >
                          {t('dashboard.admin.restaurants.deleteButton')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              </div>
            );
          })()}
        </div>

        {/* Recent Users - Admin only */}
        {isAdmin() && (
          <div className="dashboard-section">
            <h2>{t('dashboard.admin.recentUsers.title')}</h2>
            <div className="users-list">
              {allUsers.slice(0, 5).map(user => (
                <div key={user.id} className="user-item">
                  <div>
                    <strong>{user.name}</strong>
                    <span className="user-role">{user.role}</span>
                  </div>
                  <div className="user-email">{user.email}</div>
                </div>
              ))}
            </div>
            <Link to="/admin/users" className="btn btn-secondary" style={{ marginTop: '1rem' }}>
              {t('dashboard.admin.recentUsers.viewAll')}
            </Link>
          </div>
        )}
      </div>

      {/* Delete restaurant confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>{t('dashboard.admin.confirmDelete.title')}</DialogTitle>
        <DialogContent>
          {deleteConfirmRestaurant && (
            <p style={{ margin: 0 }}>
              {t('dashboard.admin.confirmDelete.message', { name: deleteConfirmRestaurant.name })}
            </p>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteRestaurantConfirm} variant="contained" color="error">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Toggle restaurant status confirmation */}
      <Dialog open={toggleConfirmOpen} onClose={() => setToggleConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>{t('dashboard.admin.confirmToggle.title', { action: toggleConfirmAction })}</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0 }}>{t('dashboard.admin.confirmToggle.message')}</p>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setToggleConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleToggleRestaurantConfirm} variant="contained" color="primary">{t('common.confirm')}</Button>
        </DialogActions>
      </Dialog>

      {/* Result message dialog */}
      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>{t('common.notice')}</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0 }}>{resultDialogMessage}</p>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setResultDialogOpen(false)} variant="contained" color="primary">{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
};

export default AdminDashboard;
